import axios from 'axios';
import _ from 'lodash';
import React, { useState } from 'react';

import { Alert, Box, Button, Col, DataGrid, defaultPaginationData, Divider, Link, Row, Select, TextField, Typography } from 'components/AORedesign';
import { API_BASE_URL } from '../../constants/api';
import { redirectUrlFunction } from '../CommonUtilityServices/RedirectService';
import { sendGetMSEMSCPlusPolicyInquiry } from './cls-utis';

const ERROR_MESSAGE = 'Error retrieving policy source. Please reach out to MSA Help Desk';
const CLMSS_POLICY_DISCLAIMER =
  'You have entered a Commercial Lines Main Street Station (CLMSS) policy number.  All CLMSS policies have successfully transitioned to Main Street Commercial+.';
const SEARCH_TYPE_POLICY_NUMBER = 'policyNumber';
const SEARCH_TYPE_BUSINESS_NAME = 'businessName';
const POLICY_SYSTEM_MSE = 'policy-inquiry-main-street-express';
const defaultSearchResult = { loading: false, data: null };

export const getMSEMSCPRedirectRequestBody = (clmsePolicyPayload) => ({
  Area: 'Commercial Lines',
  TypeOfRedirect: 'MSE Policy Inquiry',
  DetailedContext: [
    {
      name: 'system',
      value: 'SBSS',
    },
    {
      name: 'policyNumber',
      value: clmsePolicyPayload?.data[0]?.policyNumber ?? null,
    },
    {
      name: 'agencyNumber',
      value: clmsePolicyPayload?.data[0]?.agencyId ?? null,
    },
    {
      name: 'path',
      value: 'details',
    },
  ],
});

const CLSPolicyInquiry = () => {
  const [errorGetPolicyInquiry, setErrorGetPolicyInquiry] = useState(null);
  const [errorPolicyInquiryCLMSS, setErrorPolicyInquiryCLMSS] = useState(null);
  const [policyNumberNotFound, setPolicyNumberNotFound] = useState(null);
  const [policyInquiryRedirectError, setPolicyInquiryRedirectError] = useState(null);
  const [selectedSearchType, setSelectedSearchType] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [paginationData, setPaginationData] = useState(defaultPaginationData);
  const [searchResults, setSearchResults] = useState(defaultSearchResult);

  const resetAlerts = () => {
    setErrorGetPolicyInquiry(null);
    setErrorPolicyInquiryCLMSS(null);
    setPolicyNumberNotFound(null);
  };

  const resetGrid = () => {
    setSearchResults(defaultSearchResult);
    setPaginationData(defaultPaginationData);
    resetAlerts();
  };

  const getMSEMSCPlusPolicyInfoAndRedirect = async (policy_system, policyNumber) => {
    const clmsePolicyPayload = await sendGetMSEMSCPlusPolicyInquiry(
      policyNumber,
      setErrorGetPolicyInquiry,
      () => {},
      () => {},
    );
    if (!_.isNil(clmsePolicyPayload) && !_.isNil(clmsePolicyPayload.data) && clmsePolicyPayload.data.length > 0) {
      // login to sbss
      const redirect_request_body = getMSEMSCPRedirectRequestBody(clmsePolicyPayload);
      redirectUrlFunction(redirect_request_body, 'POST', null, setPolicyInquiryRedirectError, () => {});
      setSearchResults((prevState) => ({ ...prevState, loading: false }));
    } else {
      // set warning message
      if (policy_system === POLICY_SYSTEM_MSE) {
        setPolicyNumberNotFound('Policy is not available in Main Street Express/Main Street Commercial+. Please modify your search and try again.');
      } else {
        setPolicyNumberNotFound('Policy is not available in Main Street Commercial+. Please modify your search and try again.');
      }
      setSearchResults((prevState) => ({ ...prevState, loading: false }));
    }
    return clmsePolicyPayload;
  };

  const handleSearchInputChange = (event) => {
    resetAlerts();
    const searchInputValue = event.target.value;
    setSearchInputValue(searchInputValue);
  };

  const handleChangeSearchType = (event) => {
    setSelectedSearchType(event.target.value);
    resetGrid();
  };

  const renderCell = (params) => {
    if (params.row.sourceSystem === 'T') {
      const handleOnClick = () => {
        resetAlerts();
        setSearchResults((prevState) => ({ ...prevState, loading: true }));
        getMSEMSCPlusPolicyInfoAndRedirect(POLICY_SYSTEM_MSE, params.row.policyNumber);
      };
      return params.row.policyNumber && <Link onClick={handleOnClick}>{params.row.policyNumber}</Link>;
    } else if (params.row.sourceSystem === 'I') {
      return params.row.policyNumber;
    }
  };

  const clSearchResultColumns = [
    {
      field: 'policyNumber',
      headerName: 'Policy Number',
      sortable: true,
      flex: 1,
      renderCell,
    },
    {
      field: 'namedInsured',
      headerName: 'Named Insured',
      sortable: true,
      flex: 1,
    },
    {
      field: 'mailingAddress',
      headerName: 'Mailing Address',
      sortable: true,

      flex: 1,
      cellClassName: 'wrap-text',
    },
    {
      field: 'lob',
      headerName: 'LOB',
      flex: 1,
    },
    {
      field: 'agentCode',
      headerName: 'Agent Code',
      flex: 1,
    },

    {
      field: 'sourceSystem',
      header: 'Source System',
      hide: true,
    },
  ];

  const retrieveResults = async () => {
    try {
      setSearchResults((prevState) => ({ ...prevState, loading: true }));
      const results = await axios.get(`${API_BASE_URL}/cl-policy-view-change/search?searchType=${selectedSearchType}&searchValue=${searchInputValue}`);
      const searchByPolicyNumber = selectedSearchType === SEARCH_TYPE_POLICY_NUMBER;
      // const searchByBusinessName = selectedSearchType === SEARCH_TYPE_BUSINESS_NAME;
      // Default to calling MSE/MSC+ service if searching by policy number, no results are returned, and the search value is 10 characters (valid policy number)
      if (searchByPolicyNumber && results.data?.clPolicies.length === 0 && searchInputValue.trim().length === 10) {
        const msePolicySearch = await getMSEMSCPlusPolicyInfoAndRedirect(POLICY_SYSTEM_MSE, searchInputValue);
        // Display no results message if not found.
        if (msePolicySearch?.data?.length === 0) {
          setSearchResults((prevState) => ({
            ...prevState,
            loading: false,
            data: [],
          }));
        }
      }
      // Try to automatically perform action for user if only 1 result
      else if (searchByPolicyNumber && results.data?.clPolicies.length === 1) {
        resetGrid();
        if (results.data.clPolicies[0].sourceSystem === 'T') {
          getMSEMSCPlusPolicyInfoAndRedirect(POLICY_SYSTEM_MSE, results.data.clPolicies[0].policyNumber);
        } else if (results.data.clPolicies[0].sourceSystem === 'I') {
          setErrorPolicyInquiryCLMSS(CLMSS_POLICY_DISCLAIMER);
        } else if (results.data.clPolicies[0].sourceSystem === null) {
          setPolicyNumberNotFound(ERROR_MESSAGE);
        }
      } else {
        setSearchResults((prevState) => ({
          ...prevState,
          loading: false,
          data: results.data?.clPolicies?.map((row) => ({ id: row.policyNumber, ...row })),
        }));
      }
    } catch (err) {
      console.error(err);
      setErrorGetPolicyInquiry(ERROR_MESSAGE);
      resetGrid();
    }
  };

  const handleClickSearch = async () => {
    resetGrid();

    resetAlerts();
    await retrieveResults();
  };

  const handleOnPageChange = (page) => {
    setPaginationData((prevState) => ({ ...prevState, currentPage: page }));
  };

  const handleOnPageSizeChange = (pageSize) => {
    setPaginationData((prevState) => ({ ...prevState, pageSize: pageSize }));
  };

  const getTextFieldLabel = () => {
    switch (selectedSearchType) {
      case SEARCH_TYPE_POLICY_NUMBER:
        return 'Policy Number';
      case SEARCH_TYPE_BUSINESS_NAME:
        return 'Business Name';
      default:
        return 'Enter Search Input';
    }
  };

  return (
    <>
      {errorGetPolicyInquiry && (
        <Box mb={2}>
          <Alert message={errorGetPolicyInquiry} severity="error" mb={2} />
        </Box>
      )}
      {policyInquiryRedirectError && (
        <Box>
          <Alert id="cls-policy-inquiry-error-alert-component" message={policyInquiryRedirectError} severity="error" />
        </Box>
      )}
      {policyNumberNotFound && (
        <Box mb={2}>
          <Alert message={policyNumberNotFound} severity="info" mb={2} />
        </Box>
      )}
      <Box>
        <Typography id="cls-policy-inquiry-title" variant="h6" gutterBottom={true}>
          Policy Inquiry
        </Typography>
      </Box>
      <Box id="cls-policy-inquiry-row-col-typgraphy" mb={2}>
        <Box id="cls-policy-inquiry-box-typgraphy-spacer" pt={2}>
          <Box>
            <Typography gutterBottom={true}>Enter the following to view policy information.</Typography>
          </Box>
          <Box mb={2} />
          <Row id="policy-system" mb={2}>
            <Col>
              <Select
                label="Specific Search"
                displayLabel="Select Search Type"
                onChange={handleChangeSearchType}
                menuItems={[
                  { label: 'Select Search Type', value: '', disabled: true },
                  { label: 'Policy Number', value: SEARCH_TYPE_POLICY_NUMBER },
                  { label: 'Business Name', value: SEARCH_TYPE_BUSINESS_NAME },
                ]}
                name="searchType"
                value={selectedSearchType}
              />
            </Col>
            <Box mr={2} />
            <Col>
              <TextField
                id="cls-textfield-policy-number"
                onChange={handleSearchInputChange}
                value={searchInputValue}
                label={getTextFieldLabel()}
                data-testid="textfield-policy-number"
              />
            </Col>
          </Row>
          <Box>
            <Box />
            <Row id="policy-system">
              <Box mt={2}>
                <Button
                  id="cls-policy-inquiry-search-button"
                  disabled={searchInputValue.length < 2 || searchInputValue.length > 25 || selectedSearchType === '' || searchResults.loading}
                  onClick={handleClickSearch}
                  variant="contained"
                  color="primary"
                  style={{ width: '100px', height: '45px' }}>
                  Search
                </Button>
              </Box>
              <Box ml={2}>{errorPolicyInquiryCLMSS && <Alert message={errorPolicyInquiryCLMSS} severity="error" style={{ height: '35px' }} />}</Box>
            </Row>
          </Box>
          {searchResults?.data !== null && (
            <>
              <Box my={3}>{searchResults?.data.length === 0 && <Divider />} </Box>
              <DataGrid
                columns={clSearchResultColumns}
                loading={searchResults.loading}
                onPageChange={handleOnPageChange}
                onPageSizeChange={handleOnPageSizeChange}
                page={paginationData.currentPage}
                pageSize={paginationData.pageSize}
                paginationMode="client"
                rowCount={searchResults?.data?.length}
                rows={searchResults.data}
                noResultsMessage={`No results found. Adjust your search${
                  selectedSearchType === SEARCH_TYPE_BUSINESS_NAME ? ' or try entering a policy number' : ''
                }.`}
              />
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
export default CLSPolicyInquiry;
